import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, ButtonGroup, Form, Spinner } from 'react-bootstrap';
import { useOutletContext } from 'react-router-dom';
import Icon from '../../common/Icons';
import rendezVousService from '../../../services/rendezVousService';
import ReportSectionHead from '../../head-doctor/reports/ReportSectionHead';
import { formatAppTime } from '../../../utils/appTimezone';
import { buildRdvListQuery, buildRdvExportQuery, RDV_PAGE_SIZE } from '../../receptionist/rdvListQuery';
import { formatRdvServiceLabel, formatRdvSlotDay } from '../../receptionist/rdvFormatters';
import { ReceptionStatusBadge } from '../../receptionist/ui';

const PERIOD_OPTIONS = [
  { value: 'today', label: "Aujourd'hui" },
  { value: 'week', label: '7 jours' },
  { value: 'upcoming', label: 'À venir' },
  { value: 'all', label: 'Tous' }
];

export default function AdminReceptionRdvPage() {
  const { toast } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [periodScope, setPeriodScope] = useState('today');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    const t = window.setTimeout(() => setSearchDebounced(search), 400);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [periodScope, status, searchDebounced]);

  const listQuery = useMemo(
    () =>
      buildRdvListQuery({
        periodScope,
        status,
        search: searchDebounced,
        page
      }),
    [periodScope, status, searchDebounced, page]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await rendezVousService.list(listQuery);
      if (res.success) {
        setRows(Array.isArray(res.data) ? res.data : []);
        setTotalCount(
          typeof res.pagination?.total === 'number' ? res.pagination.total : res.data?.length || 0
        );
      } else {
        setRows([]);
        setTotalCount(0);
        toast?.('danger', res.message || 'Chargement impossible');
      }
    } catch (e) {
      setRows([]);
      toast?.('danger', e.message || 'Chargement des rendez-vous impossible');
    } finally {
      setLoading(false);
    }
  }, [listQuery, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const pageCount = Math.max(1, Math.ceil((totalCount || 0) / RDV_PAGE_SIZE) || 1);

  const handleExport = async () => {
    setExporting(true);
    try {
      await rendezVousService.downloadCsv(
        buildRdvExportQuery({ periodScope, status, search: searchDebounced })
      );
      toast?.('success', 'Export CSV téléchargé');
    } catch (e) {
      toast?.('danger', e.message || 'Export impossible');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <ReportSectionHead
        badge="Réception"
        title="Rapport des rendez-vous"
        description="Agenda institutionnel — filtres période / statut et export CSV."
        actions={
          <Button
            size="sm"
            variant="outline-primary"
            disabled={exporting || loading}
            onClick={handleExport}
          >
            <Icon name="download" size={14} className="me-1" aria-hidden />
            {exporting ? 'Export…' : 'Export CSV'}
          </Button>
        }
      />

      <div className="hd-reports-panel mb-3">
        <div className="d-flex flex-column flex-lg-row gap-3 align-items-lg-end">
          <div>
            <div className="text-muted small mb-1">Période</div>
            <ButtonGroup size="sm">
              {PERIOD_OPTIONS.map((opt) => (
                <Button
                  key={opt.value}
                  variant={periodScope === opt.value ? 'primary' : 'outline-secondary'}
                  onClick={() => setPeriodScope(opt.value)}
                >
                  {opt.label}
                </Button>
              ))}
            </ButtonGroup>
          </div>
          <Form.Group className="mb-0" style={{ minWidth: 160 }}>
            <Form.Label className="text-muted small mb-1">Statut</Form.Label>
            <Form.Select size="sm" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Tous</option>
              <option value="EN_ATTENTE">En attente</option>
              <option value="CONFIRME">Confirmé</option>
              <option value="TERMINE">Terminé</option>
              <option value="ANNULE">Annulé</option>
              <option value="ABSENT">Absent</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-0 flex-grow-1">
            <Form.Label className="text-muted small mb-1">Recherche</Form.Label>
            <Form.Control
              size="sm"
              placeholder="Patient, référence…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Form.Group>
          <Button size="sm" variant="outline-secondary" onClick={loadData} disabled={loading}>
            Actualiser
          </Button>
        </div>
      </div>

      <div className="hd-reports-panel">
        {loading ? (
          <div className="hd-reports-loading py-4">
            <Spinner animation="border" size="sm" />
            <span>Chargement…</span>
          </div>
        ) : rows.length === 0 ? (
          <p className="hd-reports-empty mb-0">Aucun rendez-vous sur cette sélection.</p>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Heure</th>
                    <th>Réf.</th>
                    <th>Patient</th>
                    <th>Service</th>
                    <th>Statut</th>
                    <th>Priorité</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const patient =
                      `${r.member_prenom || r.external_prenom || ''} ${r.member_nom || r.external_nom || ''}`.trim() ||
                      '—';
                    return (
                      <tr key={r.id}>
                        <td>{formatRdvSlotDay(r.start_at)}</td>
                        <td className="font-monospace">{formatAppTime(r.start_at)}</td>
                        <td className="font-monospace small">{r.reference || '—'}</td>
                        <td>{patient}</td>
                        <td>{formatRdvServiceLabel(r.service)}</td>
                        <td>
                          <ReceptionStatusBadge kind="rdvStatus" value={r.status || 'EN_ATTENTE'} />
                        </td>
                        <td>
                          {String(r.priority || '').toUpperCase() === 'URGENT' ? (
                            <ReceptionStatusBadge kind="priority" value="URGENT" />
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="d-flex justify-content-between align-items-center mt-3">
              <span className="text-muted small">
                {totalCount} rendez-vous · page {page + 1}/{pageCount}
              </span>
              <ButtonGroup size="sm">
                <Button
                  variant="outline-secondary"
                  disabled={page <= 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Précédent
                </Button>
                <Button
                  variant="outline-secondary"
                  disabled={page + 1 >= pageCount}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Suivant
                </Button>
              </ButtonGroup>
            </div>
          </>
        )}
      </div>
    </>
  );
}
